import { makeAutoObservable, reaction, runInAction } from 'mobx'
import { AxiosError } from 'axios'
import { nanoid } from 'nanoid'
import { isEmail, isLink, logger } from 'shared/lib'
import { CountryEnum, PhoneInputStore } from 'shared/ui'
import { getLuminanceHEXColor } from 'shared/lib/getLuminanceHEXColor'
import { type INormalizedApiError } from 'shared/api'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { type IRcsParams, RcsApi, type IResponseRcs } from 'entities/Rcs'
import type { IRcsNumber, IRcsWebsite, IRcsEmail } from './type'

const defaultColor = '#068ff9'
export class RcsCreateProfileStore {
  private _loading = false
  private _brandName = ''
  private _brandNameError = ''
  private _brandDescription = ''
  private _brandDescriptionError = ''
  private _accentColor = defaultColor
  private _accentColorError = ''

  private _logoFile: null | File = null
  private _logoUrl = ''
  private _logoError = ''
  private _logoLoading = false

  private _bannerFile: null | File = null
  private _bannerUrl = ''
  private _bannerError = ''
  private _bannerLoading = false

  private _numbers: Map<string, IRcsNumber> = new Map()
  private _websites: Map<string, IRcsWebsite> = new Map()
  private _emails: Map<string, IRcsEmail> = new Map()

  private _privacyPolicyUrl = ''
  private _privacyPolicyError = ''
  private _termsAndConditionsUrl = ''
  private _termsAndConditionsError = ''

  private _termsAndConditionsAgreed = false
  private _termsAndConditionsAgreedError = ''

  constructor(private _entities: IEntitiesStore) {
    this.addNumber()

    makeAutoObservable(this)
    this.updateLogoReaction()
    this.updateBannerReaction()
  }

  get hasLogoUrl() {
    return !!this._logoUrl
  }

  get hasBannerUrl() {
    return !!this._bannerUrl
  }

  get accentColorError() {
    return this._accentColorError
  }

  get termsAndConditionsAgreed() {
    return this._termsAndConditionsAgreed
  }

  get termsAndConditionsAgreedError() {
    return this._termsAndConditionsAgreedError
  }

  setTermsAndConditionsAgreed = (agreed: boolean) => {
    this._termsAndConditionsAgreed = agreed
    if (this.termsAndConditionsAgreedError) this._termsAndConditionsAgreedError = ''
  }

  get hasEmails() {
    return this._emails.size > 0
  }

  get hasWebsites() {
    return this._websites.size > 0
  }

  get canDeleteNumberOrEmail() {
    return (
      (!!this._numbers.size && !!this._emails.size) ||
      this._numbers.size > 1 ||
      this._emails.size > 1
    )
  }

  get numbers() {
    return Array.from(this._numbers.values())
  }

  get websites() {
    return Array.from(this._websites.values())
  }

  get emails() {
    return Array.from(this._emails.values())
  }

  get bannerName() {
    return this._bannerUrl.split('/').pop()
  }

  get bannerSubtitle() {
    return this.bannerName || '1440 x 448 px, max 200 KB'
  }

  get bannerLoading() {
    return this._bannerLoading
  }

  get bannerFile() {
    return this._bannerFile
  }

  get bannerUrl() {
    return this._bannerUrl
  }

  get bannerError() {
    return this._bannerError
  }

  get logoError() {
    return this._logoError
  }

  get logoUrl() {
    return this._logoUrl
  }

  get logoName() {
    return this.logoUrl.split('/').pop()
  }

  get logoSubtitle() {
    if (this.logoName) {
      return this.logoName
    }

    return '224x224 px, max 50KB'
  }

  get logoFile() {
    return this._logoFile
  }

  get loading() {
    return this._loading
  }

  get logoLoading() {
    return this._logoLoading
  }

  get brandDescription() {
    return this._brandDescription
  }

  get brandDescriptionError() {
    return this._brandDescriptionError
  }

  get brandName() {
    return this._brandName
  }

  get accentColor() {
    return this._accentColor
  }

  get canAddNumber() {
    return this._numbers.size < 3
  }

  get canAddWebsite() {
    return this._websites.size < 3
  }

  get hasNumbers() {
    return this._numbers.size > 0
  }

  get brandDescriptionMaxLength() {
    return this.brandNameMaxLength
  }

  get isBrandDescriptionMaxLengthReached() {
    return this.brandDescription.length >= this.brandDescriptionMaxLength
  }

  get brandNameMaxLength() {
    return 100
  }

  get isBrandNameMaxLengthReached() {
    return this.brandName.length >= this.brandNameMaxLength
  }

  get privacyPolicyUrl() {
    return this._privacyPolicyUrl
  }

  get termsAndConditionsUrl() {
    return this._termsAndConditionsUrl
  }

  get privacyPolicyError() {
    return this._privacyPolicyError
  }

  get termsAndConditionsError() {
    return this._termsAndConditionsError
  }

  setPrivacyPolicyUrl = (url: string) => {
    this._privacyPolicyUrl = url
    if (this.privacyPolicyError) this._privacyPolicyError = ''
  }

  setTermsAndConditionsUrl = (url: string) => {
    this._termsAndConditionsUrl = url
    if (this.termsAndConditionsError) this._termsAndConditionsError = ''
  }

  addNumber = () => {
    if (!this.canAddNumber) return

    const number: IRcsNumber = {
      id: nanoid(),
      label: '',
      labelError: '',
      phoneInputStore: new PhoneInputStore({ excludeCountries: [CountryEnum.GB, CountryEnum.AU] }),
    }

    this._numbers.set(number.id, number)
  }

  addWebsite = () => {
    if (!this.canAddWebsite) return

    const value: IRcsWebsite = {
      id: nanoid(),
      url: '',
      urlError: '',
      label: '',
      labelError: '',
    }

    this._websites.set(value.id, value)
  }

  get canAddEmail() {
    return this._emails.size < 3
  }

  addEmail = () => {
    if (!this.canAddEmail) return

    const value: IRcsEmail = {
      id: nanoid(),
      email: '',
      emailError: '',
      label: '',
      labelError: '',
    }

    this._emails.set(value.id, value)
  }

  removeWebsite = (id: string) => {
    this._websites.delete(id)
  }

  setLabel = (value: IRcsWebsite | IRcsNumber | IRcsEmail) => {
    if ('url' in value) {
      const _value = this._websites.get(value.id)
      if (_value) {
        _value.label = value.label

        if (value.labelError) {
          _value.labelError = ''
        }
      }
    }

    if ('email' in value) {
      const _value = this._emails.get(value.id)
      if (_value) {
        _value.label = value.label

        if (value.labelError) {
          _value.labelError = ''
        }
      }
    }

    if ('phoneInputStore' in value) {
      const _number = this._numbers.get(value.id)
      if (_number) {
        _number.label = value.label

        if (value.labelError) {
          _number.labelError = ''
        }
      }
    }
  }

  setValue = (value: IRcsWebsite | IRcsNumber | IRcsEmail) => {
    if ('url' in value) {
      const _value = this._websites.get(value.id)
      if (_value) {
        _value.url = value.url

        if (value.urlError) {
          _value.urlError = ''
        }
      }
    }

    if ('email' in value) {
      const _value = this._emails.get(value.id)
      if (_value) {
        _value.email = value.email

        if (value.emailError) {
          _value.emailError = ''
        }
      }
    }

    if ('phoneInputStore' in value) {
      const _number = this._numbers.get(value.id)
      if (_number) {
        _number.phoneInputStore.setNumber(value.phoneInputStore.number)

        if (value.phoneInputStore.numberError) {
          _number.phoneInputStore.setNumberError('')
        }
      }
    }
  }

  removeEmail = (id: string) => {
    this._emails.delete(id)
  }

  removeNumber = (id: string) => {
    this._numbers.delete(id)
  }

  setBannerFile = (file: File) => {
    this._bannerFile = file
    this._bannerUrl = ''
    if (this.bannerError) this._bannerError = ''
  }

  get isValidAccentColor() {
    const luminance = getLuminanceHEXColor(this.accentColor)

    return luminance <= 0.5
  }

  setAccentColor = (color: string) => {
    this._accentColor = color

    this._accentColorError = this.isValidAccentColor
      ? ''
      : 'This color is hard to read. Use a higher contrast color.'
  }

  setLogoFile = (file: File) => {
    this._logoFile = file
    this._logoUrl = ''
    if (this.logoError) this._logoError = ''
  }

  get brandNameError() {
    return this._brandNameError
  }

  setBrandName = (name: string) => {
    this._brandName = name
    if (this.brandNameError) this._brandNameError = ''
  }

  setBrandDescription = (description: string) => {
    this._brandDescription = description
    if (this.brandDescriptionError) this._brandDescriptionError = ''
  }

  get canCreate() {
    const validNumbers = this.numbers.every(
      (number) => !number.labelError && !number.phoneInputStore.numberError
    )
    const validWebsites = this.websites.every((website) => !website.labelError && !website.urlError)
    const validEmails = this.emails.every((email) => !email.labelError && !email.emailError)

    return (
      !this.brandNameError &&
      !this.brandDescriptionError &&
      !this.logoError &&
      !this.bannerError &&
      !this.privacyPolicyError &&
      !this.termsAndConditionsError &&
      validNumbers &&
      validWebsites &&
      validEmails &&
      !this.accentColorError &&
      !this.termsAndConditionsAgreedError
    )
  }

  validate = () => {
    let hasError = false

    if (!this.brandName) {
      this._brandNameError = 'Enter a brand name'
      hasError = true
    }

    if (!this.brandDescription) {
      this._brandDescriptionError = 'Enter a brand description'
      hasError = true
    }

    if (!this.logoUrl) {
      this._logoError = 'Upload logo (224×224 px, max 50KB)'
      hasError = true
    }

    if (!this.bannerUrl) {
      this._bannerError = 'Upload banner (1440×448 px, max 200KB)'
      hasError = true
    }

    if (this.hasNumbers) {
      this.numbers.forEach((number) => {
        if (!number.label) {
          number.labelError = 'Enter a label'
          hasError = true
        }

        number.phoneInputStore.triggerNumberValidation()
        if (number.phoneInputStore.numberError) hasError = true
      })
    }

    if (this.hasWebsites) {
      this.websites.forEach((website) => {
        if (!website.label) {
          website.labelError = 'Enter a label'
          hasError = true
        }

        if (!website.url || !isLink(website.url)) {
          website.urlError = 'Enter a valid URL'
          hasError = true
        }
      })
    }

    if (this.hasEmails) {
      this.emails.forEach((email) => {
        if (!email.label) {
          email.labelError = 'Enter a label'
          hasError = true
        }

        if (!email.email || !isEmail(email.email)) {
          email.emailError = 'Enter a valid email address'
          hasError = true
        }
      })
    }

    if (!this.privacyPolicyUrl || !isLink(this.privacyPolicyUrl)) {
      this._privacyPolicyError = 'Enter a valid URL'
      hasError = true
    }

    if (!this.termsAndConditionsUrl || !isLink(this.termsAndConditionsUrl)) {
      this._termsAndConditionsError = 'Enter a valid URL'
      hasError = true
    }

    if (!this.termsAndConditionsAgreed) {
      this._termsAndConditionsAgreedError = 'Confirm to continue'
      hasError = true
    }

    if (!this.isValidAccentColor) {
      this._accentColorError = 'This color is hard to read. Use a higher contrast color.'
      hasError = true
    }

    return hasError
  }

  get profileParams(): IRcsParams {
    const params: IRcsParams = {
      name: this.brandName,
      description: this.brandDescription,
      logo_path: this.logoUrl,
      accent_color: this.accentColor,
      banner_path: this.bannerUrl,
      privacy_policy_url: this.privacyPolicyUrl,
      terms_url: this.termsAndConditionsUrl,
    }

    if (this.hasNumbers) {
      params.contact_phones = this.numbers.map((number) => ({
        phone_number: number.phoneInputStore.number,
        label: number.label,
        country_code: number.phoneInputStore.country.id as string,
      }))
    }

    if (this.hasEmails) {
      params.contact_emails = this.emails.map((email) => ({
        email: email.email,
        label: email.label,
      }))
    }

    if (this.hasWebsites) {
      params.website_urls = this.websites.map((website) => ({
        website: website.url,
        label: website.label,
      }))
    }

    return params
  }

  init = (data: IResponseRcs) => {
    this._brandName = data.name || ''
    this._brandDescription = data.description || ''
    this._logoUrl = data.logo_path || ''
    this._accentColor = data.accent_color || defaultColor
    this._bannerUrl = data.banner_path || ''
    this._privacyPolicyUrl = data.privacy_policy_url || ''
    this._termsAndConditionsUrl = data.terms_url || ''
    this._numbers = new Map(
      data.contact_phones?.map((phone) => {
        const id = nanoid()

        return [
          id,
          {
            id,
            phoneInputStore: new PhoneInputStore({
              excludeCountries: [CountryEnum.GB, CountryEnum.AU],
              number: phone.phone_number,
            }),
            label: phone.label,
            labelError: '',
          },
        ]
      })
    )
    this._websites = new Map(
      data.website_urls?.map((website) => {
        const id = nanoid()

        return [
          id,
          {
            id,
            url: website.website,
            urlError: '',
            label: website.label,
            labelError: '',
          },
        ]
      })
    )
    this._emails = new Map(
      data.contact_emails?.map((email) => {
        const id = nanoid()

        return [
          id,
          {
            id,
            email: email.email,
            emailError: '',
            label: email.label,
            labelError: '',
          },
        ]
      })
    )
  }

  uploadBanner = async () => {
    if (!this._bannerFile) return

    try {
      runInAction(() => {
        this._bannerLoading = true
      })

      const formData = new FormData()
      formData.append('file', this._bannerFile)

      const {
        data: { url },
      } = await RcsApi.uploadBanner(formData)

      runInAction(() => {
        this._bannerUrl = url
        this._bannerFile = null
      })
    } catch (e) {
      logger.error(e)

      if (e instanceof AxiosError) {
        const errorMessage = e.errors.getError('file') || 'Failed to upload large banner'
        runInAction(() => {
          this._bannerError = errorMessage
        })
      }
    } finally {
      runInAction(() => {
        this._bannerLoading = false
      })
    }
  }

  uploadLogo = async () => {
    if (!this.logoFile) return

    try {
      runInAction(() => {
        this._logoLoading = true
      })

      const formData = new FormData()
      formData.append('file', this.logoFile)

      const {
        data: { url },
      } = await RcsApi.uploadLogo(formData)

      runInAction(() => {
        this._logoUrl = url
        this._logoFile = null
      })
    } catch (e) {
      logger.error(e)

      if (e instanceof AxiosError) {
        const errorMessage = e.errors.getError('file') || 'Failed to upload logo'
        runInAction(() => {
          this._logoError = errorMessage
        })
      }
    } finally {
      runInAction(() => {
        this._logoLoading = false
      })
    }
  }

  removeLogo = async () => {
    const defaultError = 'Failed to remove logo'

    try {
      runInAction(() => {
        this._logoLoading = true
      })

      const {
        data: { deleted },
      } = await RcsApi.removeLogo({ url: this.logoUrl })

      runInAction(() => {
        if (deleted) {
          this._logoUrl = ''
          this._logoFile = null
        } else {
          this._logoError = defaultError
        }
      })
    } catch (error) {
      logger.error(error)

      if (error instanceof AxiosError) {
        const errorMessage = error.errors.getError('file') || defaultError
        runInAction(() => {
          this._logoError = errorMessage
        })
      }
    } finally {
      runInAction(() => {
        this._logoLoading = false
      })
    }
  }

  removeBanner = async () => {
    const defaultError = 'Failed to remove large banner'

    try {
      runInAction(() => {
        this._bannerLoading = true
      })

      const {
        data: { deleted },
      } = await RcsApi.removeBanner({ url: this._bannerUrl })

      runInAction(() => {
        if (deleted) {
          this._bannerUrl = ''
          this._bannerFile = null
        } else {
          this._bannerError = defaultError
        }
      })
    } catch (error) {
      logger.error(error)

      if (error instanceof AxiosError) {
        const errorMessage = error.errors.getError('file') || defaultError
        runInAction(() => {
          this._bannerError = errorMessage
        })
      }
    } finally {
      runInAction(() => {
        this._bannerLoading = false
      })
    }
  }

  initErrors = (errors: INormalizedApiError) => {
    this._brandNameError = errors.getError('name')
    this._brandDescriptionError = errors.getError('description')
    this._logoError = errors.getError('logo_path')
    this._bannerError = errors.getError('banner_path')
    this._privacyPolicyError = errors.getError('privacy_policy_url')
    this._termsAndConditionsError = errors.getError('terms_url')
    this.numbers.forEach((value, index) => {
      const number = this._numbers.get(value.id)
      if (!number) return
      number.labelError = errors.getError(`contact_phones.${index}.label`)
      number.phoneInputStore.setNumberError(errors.getError(`contact_phones.${index}.phone_number`))
    })
    this.emails.forEach((value, index) => {
      const email = this._emails.get(value.id)
      if (!email) return
      email.labelError = errors.getError(`contact_emails.${index}.label`)
      email.emailError = errors.getError(`contact_emails.${index}.email`)
    })
    this.websites.forEach((value, index) => {
      const website = this._websites.get(value.id)
      if (!website) return
      website.labelError = errors.getError(`website_urls.${index}.label`)
      website.urlError = errors.getError(`website_urls.${index}.website`)
    })
  }

  reset = () => {
    this._loading = false
  }

  dispose = () => {}

  updateLogoReaction = () => {
    reaction(
      () => this.logoFile,
      (value) => {
        if (value) this.uploadLogo()
      }
    )
  }

  updateBannerReaction = () => {
    reaction(
      () => this._bannerFile,
      (value) => {
        if (value) this.uploadBanner()
      }
    )
  }
}
